import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Users, Clock, CheckCircle, Calendar, Building, Loader2, Search, X, FileText, Target, Briefcase, Award, MessageSquare, TrendingUp, Zap, Shield, Eye, UserCheck, Linkedin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import PageBanner from '@/components/shared/PageBanner';
import { toast } from 'sonner';

// Icon mapping for workshop topics
const iconMap = {
  FileText, Target, Briefcase, Award, MessageSquare, TrendingUp, Zap, Shield, Eye, UserCheck, Linkedin, Users, Clock, Calendar, Building, CheckCircle, Search
};

const WorkshopsPage = () => {
  const [searchParams] = useSearchParams();
  const [workshopTopics, setWorkshopTopics] = useState([]);
  const [workshopPricingMatrix, setWorkshopPricingMatrix] = useState({ durations: [], groupSizes: [] });
  const [loading, setLoading] = useState(true);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Form state
  const [formData, setFormData] = useState({
    workshopId: '',
    duration: '',
    groupSize: '',
    name: '',
    email: '',
    phone: '',
    organization: '',
    preferredDate: '',
    message: '',
    attendeeCount: ''
  });

  // Fetch workshop data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [topicsRes, pricingRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workshop-topics`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workshop-pricing`)
        ]);
        
        if (topicsRes.ok) {
          const topics = await topicsRes.json();
          setWorkshopTopics(topics || []);
        }
        
        if (pricingRes.ok) {
          const pricing = await pricingRes.json();
          // Map snake_case from API to camelCase expected by frontend
          setWorkshopPricingMatrix({
            durations: pricing?.durations || [],
            groupSizes: pricing?.group_sizes || pricing?.groupSizes || []
          });
        }
      } catch (error) {
        console.error('Error fetching workshop data:', error);
        toast.error('Failed to load workshop data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Generate category counts dynamically
  const workshopCategories = useMemo(() => {
    const categories = [
      { id: 'all', label: 'All Topics', count: workshopTopics.length },
    ];
    
    // Get unique categories from topics
    const uniqueCategories = [...new Set(workshopTopics.map(w => w.category).filter(Boolean))];
    uniqueCategories.forEach(cat => {
      const count = workshopTopics.filter(w => w.category === cat).length;
      if (count > 0) {
        categories.push({
          id: cat,
          label: cat.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
          count
        });
      }
    });
    
    return categories;
  }, [workshopTopics]);

  // Filtered workshops based on search and category
  const filteredWorkshops = useMemo(() => {
    return workshopTopics.filter(workshop => {
      const matchesSearch = searchQuery === '' || 
        workshop.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        workshop.short_description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (workshop.outcomes || []).some(o => o.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesCategory = selectedCategory === 'all' || workshop.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [workshopTopics, searchQuery, selectedCategory]);

  // Get active categories (those with at least 1 workshop)
  const activeCategories = workshopCategories.filter(cat => cat.id === 'all' || cat.count > 0);

  // Check for payment success on page load
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`);
      if (!response.ok) throw new Error('Failed to check payment status');

      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setPaymentSuccess(true);
        toast.success('Payment successful! Thank you for booking.');
        return;
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const handleOpenCheckout = (workshop) => {
    setSelectedWorkshop(workshop);
    setFormData(prev => ({ ...prev, workshopId: workshop.id }));
    setShowCheckoutModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const calculateWorkshopPrice = (durationId, groupSizeId) => {
    const duration = workshopPricingMatrix.durations?.find(d => d.id === durationId);
    const groupSize = workshopPricingMatrix.groupSizes?.find(g => g.id === groupSizeId);
    
    if (!duration || !groupSize) return null;
    
    return Math.round(groupSize.base_price * duration.multiplier);
  };

  const calculatePrice = () => {
    if (!formData.duration || !formData.groupSize) return null;
    return calculateWorkshopPrice(formData.duration, formData.groupSize);
  };

  const handleCheckout = async () => {
    const price = calculatePrice();
    if (!price) {
      toast.error('Please select duration and group size');
      return;
    }

    if (!formData.name || !formData.email || !formData.phone || !formData.organization) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCheckoutLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/workshop/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          amount: price,
          workshopTitle: selectedWorkshop.title,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) throw new Error('Failed to create checkout session');

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      toast.error('Failed to initiate checkout. Please try again.');
      console.error('Checkout error:', error);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const price = calculatePrice();

  // Get icon component from name
  const getIcon = (iconName) => {
    return iconMap[iconName] || FileText;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  // Success State
  if (paymentSuccess) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1">
          <PageBanner 
            title="Workshops & Training" 
            breadcrumbs={[{ label: 'Services', path: '/services' }, { label: 'Workshops' }]} 
          />
          <section className="py-16">
            <div className="max-w-2xl mx-auto px-4 text-center">
              <div className="bg-green-50 border border-green-200 rounded-2xl p-10">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
                <h2 className="text-2xl font-heading font-bold mb-4">Workshop Booked Successfully!</h2>
                <p className="text-muted-foreground mb-6">
                  Thank you for your booking. You will receive a confirmation email shortly with all the details.
                  Our team will contact you within 1-2 business days to finalize the workshop schedule.
                </p>
                <Button asChild>
                  <Link to="/services">Back to Services</Link>
                </Button>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1">
        <PageBanner 
          title="Workshops & Training" 
          breadcrumbs={[{ label: 'Services', path: '/services' }, { label: 'Workshops' }]} 
        />

        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-primary/5 to-accent/5" data-testid="workshops-hero">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
                  Professional Development for Your Organization
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Invest in your team's growth with our expert-led workshops. From resume writing to leadership development, 
                  we deliver engaging, actionable training customized to your organization's needs.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <span className="font-medium">Groups of any size</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-primary" />
                    </div>
                    <span className="font-medium">Flexible duration</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Building className="w-5 h-5 text-primary" />
                    </div>
                    <span className="font-medium">Virtual or on-site</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-primary" />
                    </div>
                    <span className="font-medium">Custom scheduling</span>
                  </div>
                </div>
                <Button asChild size="lg">
                  <Link to="/workshop/request">
                    Request Custom Workshop <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
              </div>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://customer-assets.emergentagent.com/job_8056591d-682d-433c-8623-ad0e4f24d171/artifacts/r2bk86co_IMG-8395.jpg"
                    alt="Professional workshop training session"
                    className="w-full h-auto"
                  />
                </div>
                <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-primary rounded-xl -z-10"></div>
                <div className="absolute -top-4 -right-4 w-16 h-16 bg-accent rounded-xl -z-10"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Overview */}
        {workshopPricingMatrix.durations?.length > 0 && workshopPricingMatrix.groupSizes?.length > 0 && (
          <section className="py-16" data-testid="pricing-overview">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                  Workshop Options
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Our pricing is based on workshop duration and group size. Request a quote and let us know your duration and expected group size so we can create a customized proposal for your organization.
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-primary text-white">
                      <tr>
                        <th className="px-6 py-4 text-left font-heading">Duration</th>
                        {workshopPricingMatrix.groupSizes.map(size => (
                          <th key={size.id} className="px-6 py-4 text-center font-heading">
                            {size.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {workshopPricingMatrix.durations.map((duration, index) => (
                        <tr key={duration.id} className={index % 2 === 0 ? 'bg-muted/30' : ''}>
                          <td className="px-6 py-4 font-medium">
                            {duration.label}
                            <span className="block text-sm text-muted-foreground">
                              {duration.minutes} minutes
                            </span>
                          </td>
                          {workshopPricingMatrix.groupSizes.map(size => (
                            <td key={size.id} className="px-6 py-4 text-center">
                              <CheckCircle className="w-5 h-5 text-primary mx-auto" />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="p-6 bg-muted/30 text-center">
                  <p className="text-muted-foreground mb-4">Ready to discuss your workshop needs?</p>
                  <Button asChild size="lg">
                    <Link to="/workshop/request">
                      Request a Quote <ArrowRight className="w-5 h-5 ml-2" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Unified Workshop Topics Section */}
        <section className="py-16 bg-muted/30" data-testid="workshop-topics">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                Workshop Topics
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Explore our comprehensive workshop topics. Each topic includes detailed learning outcomes, 
                and many have dedicated resource pages with additional strategies and tips.
              </p>
            </div>

            {/* Search and Filter Controls */}
            <div className="mb-8 space-y-4">
              {/* Search Bar */}
              <div className="relative max-w-xl mx-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search workshop topics..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-10 h-12 text-base rounded-full border-2 focus:border-primary"
                  data-testid="workshop-search"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>

              {/* Category Filter Pills */}
              <div className="flex flex-wrap justify-center gap-2">
                {activeCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedCategory === category.id
                        ? 'bg-primary text-white shadow-md'
                        : 'bg-white text-muted-foreground hover:bg-primary/10 hover:text-primary border'
                    }`}
                    data-testid={`category-${category.id}`}
                  >
                    {category.label}
                    {category.id !== 'all' && (
                      <span className={`ml-1.5 text-xs ${selectedCategory === category.id ? 'text-white/80' : 'text-muted-foreground/60'}`}>
                        ({category.count})
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Results Count */}
              {(searchQuery || selectedCategory !== 'all') && (
                <p className="text-center text-sm text-muted-foreground">
                  Showing {filteredWorkshops.length} of {workshopTopics.length} topics
                  {searchQuery && <span> matching "<strong>{searchQuery}</strong>"</span>}
                  {selectedCategory !== 'all' && (
                    <span> in <strong>{activeCategories.find(c => c.id === selectedCategory)?.label}</strong></span>
                  )}
                </p>
              )}
            </div>

            {/* Workshop Cards Grid */}
            {filteredWorkshops.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredWorkshops.map((workshop) => {
                  const IconComponent = getIcon(workshop.icon);
                  return (
                    <div 
                      key={workshop.id}
                      className="bg-white rounded-xl p-6 shadow-md border hover:shadow-lg transition-all flex flex-col"
                      data-testid={`topic-${workshop.slug}`}
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl flex items-center justify-center mb-4">
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-xl font-heading font-bold mb-2">{workshop.title}</h3>
                      <p className="text-muted-foreground text-sm mb-4">{workshop.short_description}</p>
                      
                      <div className="mb-4 flex-1">
                        <h4 className="text-sm font-medium mb-2">What You'll Learn:</h4>
                        <ul className="space-y-1">
                          {(workshop.outcomes || []).slice(0, 3).map((outcome, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                              <span>{outcome}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="flex gap-2 mt-auto pt-4 border-t">
                        {workshop.has_detail_page && (
                          <Button variant="outline" asChild className="flex-1">
                            <Link to={`/workshop/${workshop.slug}`}>
                              Learn More
                            </Link>
                          </Button>
                        )}
                        <Button onClick={() => handleOpenCheckout(workshop)} className="flex-1">
                          Book Now
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground text-lg">No workshops found matching your search.</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary text-white" data-testid="workshops-cta">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
              Don't See What You Need?
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              We specialize in creating custom workshops tailored to your organization's specific challenges and goals. 
              Let's discuss how we can help your team grow.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="secondary" size="lg" asChild>
                <Link to="/workshop/request">
                  Request Custom Workshop
                </Link>
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10" asChild>
                <Link to="/contact">
                  Contact Us
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Checkout Modal */}
      <Dialog open={showCheckoutModal} onOpenChange={setShowCheckoutModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Book Workshop: {selectedWorkshop?.title}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Workshop Options */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Duration *</Label>
                <Select value={formData.duration} onValueChange={(v) => handleSelectChange('duration', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    {workshopPricingMatrix.durations?.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Group Size *</Label>
                <Select value={formData.groupSize} onValueChange={(v) => handleSelectChange('groupSize', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent>
                    {workshopPricingMatrix.groupSizes?.map(g => (
                      <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Price Display */}
            {price && (
              <div className="bg-primary/10 rounded-lg p-4 text-center">
                <p className="text-sm text-muted-foreground">Estimated Price</p>
                <p className="text-2xl font-bold text-primary">${price}</p>
              </div>
            )}

            {/* Contact Information */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input name="name" value={formData.name} onChange={handleInputChange} placeholder="John Doe" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="john@company.com" />
                </div>
                <div className="space-y-2">
                  <Label>Phone *</Label>
                  <Input name="phone" value={formData.phone} onChange={handleInputChange} placeholder="(555) 555-5555" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Organization *</Label>
                <Input name="organization" value={formData.organization} onChange={handleInputChange} placeholder="Company Name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Preferred Date</Label>
                  <Input name="preferredDate" type="date" value={formData.preferredDate} onChange={handleInputChange} />
                </div>
                <div className="space-y-2">
                  <Label>Attendee Count</Label>
                  <Input name="attendeeCount" type="number" value={formData.attendeeCount} onChange={handleInputChange} placeholder="e.g., 25" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Additional Information</Label>
                <Textarea name="message" value={formData.message} onChange={handleInputChange} placeholder="Any specific requirements or questions..." rows={3} />
              </div>
            </div>

            <Button onClick={handleCheckout} disabled={checkoutLoading || !price} className="w-full" size="lg">
              {checkoutLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
              ) : (
                <>Proceed to Payment {price && `- $${price}`}</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkshopsPage;
